/**
 * purposeApi.ts
 *
 * Typed client for the graph service's Purpose routes (EAINT-995): the read, the parent
 * change with its preview, "also serves" links, the ledger-backed statement and affirm, and
 * the unplaced list. Route contracts and error codes: docs/packages/services/graph-service/purpose-api.md.
 *
 * Every HTTP failure is rethrown as a `PurposeApiError` carrying the status, the route's
 * `error` code and, on a stale-revision 409, the current revision — callers branch on `code`
 * rather than parsing messages. Failures with no HTTP response (offline) pass through as-is.
 */
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { PurposeNearby, PurposeParentPreview, SpherePurpose, UnplacedSpheresPage } from 'types/purpose';

export type PurposeErrorCode =
  | 'purpose_cycle'
  | 'stale_revision'
  | 'manage_security_required'
  | 'permission_service_unreachable'
  | 'requires_edit'
  | 'requires_person_author'
  | 'purpose_authoring_requires_person'
  | 'forbidden'
  | 'unplaced_with_parent'
  | 'parent_not_found_or_not_discoverable'
  | 'target_not_found_or_not_discoverable'
  | 'too_many_purpose_links'
  | 'already_linked'
  | 'link_not_found'
  | 'sphere_not_found'
  | 'no_statement'
  | 'invalid_statement'
  | 'invalid_cursor'
  | 'internal_error'
  | 'unknown';

export class PurposeApiError extends Error {
  readonly status: number | undefined;
  readonly code: PurposeErrorCode;
  /** Present on a `stale_revision` 409: the revision the caller should re-read against. */
  readonly currentRevision: string | undefined;

  constructor(status: number | undefined, code: PurposeErrorCode, currentRevision?: string) {
    const statusSuffix = status ? ` (${status})` : '';
    super(`Purpose request failed${statusSuffix}: ${code}`);
    this.name = 'PurposeApiError';
    this.status = status;
    this.code = code;
    this.currentRevision = currentRevision;
  }
}

interface ErrorLike {
  response?: { status?: number; data?: { error?: unknown; currentRevision?: unknown } };
  status?: number;
}

function toPurposeError(err: unknown): unknown {
  if (err instanceof PurposeApiError) return err;
  const e = err as ErrorLike | undefined;
  const status = e?.response?.status ?? e?.status;
  if (status === undefined) return err;
  const data = e?.response?.data;
  const code = typeof data?.error === 'string' ? (data.error as PurposeErrorCode) : 'unknown';
  const rawRevision = data?.currentRevision;
  const currentRevision =
    typeof rawRevision === 'string' || typeof rawRevision === 'number' || typeof rawRevision === 'boolean'
      ? String(rawRevision)
      : undefined;
  return new PurposeApiError(status, code, currentRevision);
}

async function guarded<T>(request: () => Promise<T>): Promise<T> {
  try {
    return await request();
  } catch (err) {
    throw toPurposeError(err);
  }
}

const sphereBase = (sphereId: string): string => `/spheres/${encodeURIComponent(sphereId)}/purpose`;

/** `GET /spheres/:id/purpose` — the path, statement, links, capabilities and revision. */
export function getSpherePurpose(graphClient: ApiClient, sphereId: string): Promise<SpherePurpose> {
  return guarded(() => graphClient.get<SpherePurpose>(sphereBase(sphereId)));
}

export interface SetPurposeParentInput {
  /** The new Purpose parent, or `null` for "sits directly in its Center". */
  parentId: string | null;
  /** Marks the sphere as needing a home; valid only with `parentId: null`. */
  unplaced?: boolean;
  baseRevision?: string;
}

/** `PUT /spheres/:id/purpose/parent` — the route schema requires `unplaced`, so it is always sent. */
export function setSpherePurposeParent(graphClient: ApiClient, sphereId: string, input: SetPurposeParentInput): Promise<SpherePurpose> {
  return guarded(() =>
    graphClient.put<SpherePurpose>(`${sphereBase(sphereId)}/parent`, {
      parentId: input.parentId,
      unplaced: input.unplaced ?? false,
      ...(input.baseRevision === undefined ? {} : { baseRevision: input.baseRevision }),
    })
  );
}

/** `POST /spheres/:id/purpose/parent/preview` — who would gain or lose access; writes nothing. */
export function previewSpherePurposeParent(graphClient: ApiClient, sphereId: string, parentId: string | null): Promise<PurposeParentPreview> {
  return guarded(() => graphClient.post<PurposeParentPreview>(`${sphereBase(sphereId)}/parent/preview`, { parentId }));
}

/** `POST /spheres/:id/purpose/links` — adds an "also serves" link; returns the refreshed payload. */
export function addSpherePurposeLink(graphClient: ApiClient, sphereId: string, targetId: string): Promise<SpherePurpose> {
  return guarded(() => graphClient.post<SpherePurpose>(`${sphereBase(sphereId)}/links`, { targetId }));
}

/** `DELETE /spheres/:id/purpose/links/:relationshipId` — returns the refreshed payload. */
export function removeSpherePurposeLink(graphClient: ApiClient, sphereId: string, relationshipId: string): Promise<SpherePurpose> {
  return guarded(() => graphClient.delete<SpherePurpose>(`${sphereBase(sphereId)}/links/${encodeURIComponent(relationshipId)}`));
}

/**
 * `PUT /spheres/:id/purpose/statement` — one ledger observation per changed write. `text: null`
 * removes the statement and its stance (what Undo of a first save sends).
 */
export function setSpherePurposeStatement(
  graphClient: ApiClient,
  sphereId: string,
  text: string | null,
  baseRevision?: string
): Promise<SpherePurpose> {
  return guarded(() =>
    graphClient.put<SpherePurpose>(`${sphereBase(sphereId)}/statement`, {
      text,
      ...(baseRevision === undefined ? {} : { baseRevision }),
    })
  );
}

/** `POST /spheres/:id/purpose/affirm` — 422 `no_statement` when there is nothing to affirm. */
export function affirmSpherePurpose(graphClient: ApiClient, sphereId: string, baseRevision?: string): Promise<SpherePurpose> {
  return guarded(() => graphClient.post<SpherePurpose>(`${sphereBase(sphereId)}/affirm`, baseRevision === undefined ? {} : { baseRevision }));
}

export interface ListUnplacedOptions {
  cursor?: string;
  limit?: number;
}

/** `GET /spheres/:centerId/purpose/unplaced` — one page of spheres in the Center that need a home. */
export function listUnplacedSpheres(graphClient: ApiClient, centerId: string, options: ListUnplacedOptions = {}): Promise<UnplacedSpheresPage> {
  const params: Record<string, string | number> = {};
  if (options.cursor !== undefined) params.cursor = options.cursor;
  if (options.limit !== undefined) params.limit = options.limit;
  return guarded(() => graphClient.get<UnplacedSpheresPage>(`${sphereBase(centerId)}/unplaced`, { params }));
}

/**
 * `GET /spheres/:id/purpose/nearby` — the Purpose-tree spheres beside and beneath this one,
 * for the parent picker's nearby buckets. A convenience read: it is capped server-side and
 * carries no `truncated` flag, because the picker's org-wide search reaches the rest.
 */
export function getSpherePurposeNearby(graphClient: ApiClient, sphereId: string): Promise<PurposeNearby> {
  return guarded(() => graphClient.get<PurposeNearby>(`${sphereBase(sphereId)}/nearby`));
}
